import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

type LogDateItem = {
  date: string;
  sizeBytes: number;
  fileName: string;
};

type ErrorLogEntry = {
  id: string;
  ts: string;
  level: 'error' | 'warn' | 'info';
  message: string;
  detail?: string;
  stack?: string;
  code?: string;
  source?: string;
  statusCode?: number;
  request?: {
    method?: string;
    path?: string;
    tenantId?: string | null;
    ip?: string | null;
  };
};

type DayResult = {
  date: string;
  total: number;
  offset: number;
  limit: number;
  items: ErrorLogEntry[];
};

function formatBytes(n: number): string {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-AR');
  } catch {
    return iso;
  }
}

function levelClass(level: string): string {
  if (level === 'error') return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  if (level === 'warn') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200';
}

function axiosMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (msg) return msg;
  }
  return err instanceof Error ? err.message : fallback;
}

export const LogsPage: React.FC = () => {
  const { t } = useTranslation();
  const { canAdminister, canOperateBilling } = useAuth();
  const canView = canAdminister || canOperateBilling;
  const { error: showError } = useToast();
  const [dates, setDates] = useState<LogDateItem[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [level, setLevel] = useState<'all' | 'error' | 'warn'>('all');
  const [page, setPage] = useState(0);
  const pageSize = 50;
  const [day, setDay] = useState<DayResult | null>(null);
  const [selected, setSelected] = useState<ErrorLogEntry | null>(null);
  const [loadingDates, setLoadingDates] = useState(true);
  const [loadingDay, setLoadingDay] = useState(false);

  const loadDates = useCallback(async () => {
    setLoadingDates(true);
    try {
      const res = await httpClient.get<{ dates: LogDateItem[] }>('/logs');
      const list = res.data.dates ?? [];
      setDates(list);
      setSelectedDate((prev) => prev || list[0]?.date || '');
    } catch (err) {
      showError(axiosMessage(err, t('pages.logs.loadError')));
      setDates([]);
    } finally {
      setLoadingDates(false);
    }
  }, [showError, t]);

  const loadDay = useCallback(async () => {
    if (!selectedDate) {
      setDay(null);
      return;
    }
    setLoadingDay(true);
    try {
      const res = await httpClient.get<DayResult>(`/logs/${encodeURIComponent(selectedDate)}`, {
        params: {
          limit: pageSize,
          offset: page * pageSize,
          level: level === 'all' ? undefined : level,
          search: appliedSearch.trim() || undefined,
        },
      });
      setDay(res.data);
    } catch (err) {
      showError(axiosMessage(err, t('pages.logs.loadError')));
      setDay(null);
    } finally {
      setLoadingDay(false);
    }
  }, [selectedDate, page, level, appliedSearch, showError, t]);

  useEffect(() => {
    if (canView) void loadDates();
  }, [canView, loadDates]);

  useEffect(() => {
    if (canView && selectedDate) void loadDay();
  }, [canView, selectedDate, loadDay]);

  const totalPages = useMemo(() => {
    if (!day) return 0;
    return Math.max(1, Math.ceil(day.total / pageSize));
  }, [day]);

  if (!canView) {
    return (
      <div className="panel-page">
        <h1 className="panel-page-title">{t('pages.logs.title')}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{t('pages.logs.forbidden')}</p>
      </div>
    );
  }

  return (
    <div className="panel-page h-full min-w-0 flex flex-col overflow-auto gap-4">
      <div className="min-w-0 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="panel-page-title">{t('pages.logs.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('pages.logs.subtitle')}</p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            void loadDates();
            void loadDay();
          }}
          disabled={loadingDates || loadingDay}
        >
          {t('pages.logs.refresh')}
        </Button>
      </div>
      <div className="panel-card flex flex-wrap gap-3 items-end">
        <label className="flex flex-col gap-1 text-xs text-gray-500 dark:text-gray-400 min-w-[10rem]">
          {t('pages.logs.date')}
          <select
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-2 py-1.5"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setPage(0);
              setSelected(null);
            }}
            disabled={loadingDates || dates.length === 0}
          >
            {dates.length === 0 && <option value="">{t('pages.logs.noDates')}</option>}
            {dates.map((d) => (
              <option key={d.date} value={d.date}>
                {d.date} ({formatBytes(d.sizeBytes)})
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-gray-500 dark:text-gray-400 min-w-[8rem]">
          {t('pages.logs.level')}
          <select
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-2 py-1.5"
            value={level}
            onChange={(e) => {
              setLevel(e.target.value as 'all' | 'error' | 'warn');
              setPage(0);
            }}
          >
            <option value="all">{t('pages.logs.levelAll')}</option>
            <option value="error">error</option>
            <option value="warn">warn</option>
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-gray-500 dark:text-gray-400 flex-1 min-w-[12rem]">
          {t('pages.logs.search')}
          <input
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-2 py-1.5"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(0);
                setAppliedSearch(search);
              }
            }}
            placeholder={t('pages.logs.searchPlaceholder')}
          />
        </label>

        <Button
          variant="primary"
          onClick={() => {
            setPage(0);
            setAppliedSearch(search);
          }}
          disabled={loadingDay || !selectedDate}
        >
          {t('pages.logs.apply')}
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 min-h-0 flex-1">
        <div className="xl:col-span-3 panel-table-wrap min-h-[20rem]">
          {loadingDay ? (
            <p className="p-4 text-sm text-gray-500">{t('common.loading')}</p>
          ) : !day || day.items.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">{t('pages.logs.empty')}</p>
          ) : (
            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900">
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2">{t('pages.logs.columns.time')}</th>
                  <th className="px-3 py-2">{t('pages.logs.columns.level')}</th>
                  <th className="px-3 py-2">{t('pages.logs.columns.message')}</th>
                  <th className="px-3 py-2">{t('pages.logs.columns.path')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {day.items.map((item) => (
                  <tr
                    key={item.id}
                    className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 ${
                      selected?.id === item.id ? 'bg-teal-50 dark:bg-teal-900/20' : ''
                    }`}
                    onClick={() => setSelected(item)}
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-gray-300">
                      {formatTs(item.ts)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${levelClass(
                          item.level
                        )}`}
                      >
                        {item.level}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100 max-w-[18rem] truncate">
                      {item.message}
                      {item.detail ? (
                        <span className="block text-xs text-gray-500 truncate">{item.detail}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-[12rem] truncate font-mono text-xs">
                      {item.request?.method
                        ? `${item.request.method} ${item.request.path ?? ''}`
                        : item.source ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {day && day.total > 0 && (
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500">
              <span>
                {t('pages.logs.pagination', {
                  total: day.total,
                  page: page + 1,
                  pages: totalPages,
                })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  disabled={page <= 0 || loadingDay}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  {t('common.previous')}
                </Button>
                <Button
                  variant="secondary"
                  disabled={page + 1 >= totalPages || loadingDay}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="xl:col-span-2 panel-card min-h-[20rem] overflow-auto">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
            {t('pages.logs.detail')}
          </h2>
          {!selected ? (
            <p className="text-sm text-gray-500">{t('pages.logs.selectHint')}</p>
          ) : (
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-xs text-gray-500">{t('pages.logs.columns.time')}</div>
                <div>{formatTs(selected.ts)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">{t('pages.logs.columns.level')}</div>
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${levelClass(selected.level)}`}>
                  {selected.level}
                </span>
              </div>
              {selected.statusCode != null && (
                <div>
                  <div className="text-xs text-gray-500">HTTP</div>
                  <div>{selected.statusCode}</div>
                </div>
              )}
              {selected.code && (
                <div>
                  <div className="text-xs text-gray-500">Code</div>
                  <div className="font-mono text-xs">{selected.code}</div>
                </div>
              )}
              <div>
                <div className="text-xs text-gray-500">{t('pages.logs.columns.message')}</div>
                <div className="whitespace-pre-wrap break-words">{selected.message}</div>
              </div>
              {selected.detail && (
                <div>
                  <div className="text-xs text-gray-500">{t('pages.logs.detailField')}</div>
                  <pre className="mt-1 text-xs bg-gray-50 dark:bg-gray-900/60 p-2 rounded overflow-auto whitespace-pre-wrap break-words">
                    {selected.detail}
                  </pre>
                </div>
              )}
              {selected.request && (
                <div>
                  <div className="text-xs text-gray-500">{t('pages.logs.request')}</div>
                  <pre className="mt-1 text-xs bg-gray-50 dark:bg-gray-900/60 p-2 rounded overflow-auto">
                    {JSON.stringify(selected.request, null, 2)}
                  </pre>
                </div>
              )}
              {selected.stack && (
                <div>
                  <div className="text-xs text-gray-500">Stack</div>
                  <pre className="mt-1 text-xs bg-gray-50 dark:bg-gray-900/60 p-2 rounded overflow-auto max-h-64">
                    {selected.stack}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
